import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

// 安全页面容器
const SecurityContainer = styled('div', {
  maxWidth: '1000px',
  margin: '0 auto',
  padding: '2rem 1rem',
});

// 页面标题样式
const PageTitle = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  textAlign: 'center',
  color: '$primary',
});

// 页面副标题样式
const PageSubtitle = styled('p', {
  fontSize: '1.2rem',
  marginBottom: '3rem',
  textAlign: 'center',
  color: '$textSecondary',
  maxWidth: '800px',
  margin: '0 auto 3rem',
});

// 安全特性卡片容器
const SecurityFeaturesGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '2rem',
  marginBottom: '3rem',
  
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  }
});

// 安全特性卡片
const SecurityFeatureCard = styled(motion.div, {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  padding: '2rem',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

// 特性图标容器
const FeatureIconContainer = styled('div', {
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: '$secondaryLight',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '1.5rem',
  color: '$secondary',
  fontSize: '1.8rem',
});

// 特性标题
const FeatureTitle = styled('h3', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  color: '$textPrimary',
});

// 特性描述
const FeatureDescription = styled('p', {
  fontSize: '1rem',
  lineHeight: '1.6',
  color: '$textSecondary',
});

// 安全提示部分
const SecurityTipsSection = styled('div', {
  background: '$backgroundAlt',
  borderRadius: '12px',
  padding: '2rem',
  marginBottom: '3rem',
});

// 提示标题
const TipsTitle = styled('h2', {
  fontSize: '1.8rem',
  fontWeight: 'bold',
  marginBottom: '1.5rem',
  color: '$textPrimary',
  textAlign: 'center',
});

// 提示列表
const TipsList = styled('ul', {
  listStyleType: 'none',
  padding: '0',
  margin: '0',
});

// 提示项目
const TipItem = styled('li', {
  display: 'flex',
  alignItems: 'flex-start',
  margin: '1rem 0',
  padding: '0.5rem 0',
  
  '&:not(:last-child)': {
    borderBottom: '1px solid $border',
  }
});

// 提示项目数字
const TipNumber = styled('span', {
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  background: '$primary',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '1rem',
  flexShrink: 0,
});

// 提示文本
const TipText = styled('div', {
  flex: '1',
});

/**
 * Kipa钱包安全页面组件
 * 展示Kipa钱包的安全特性和提示
 */
const KipaWalletSecurity: React.FC = () => {
  const { t } = useTranslation();
  
  // 页面动画变体
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1 
      }
    }
  };
  
  // 卡片动画变体
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };
  
  return (
    <SecurityContainer as={motion.div} 
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <PageTitle>{t('products.kipaWallet.security.title', 'Kipa钱包安全特性')}</PageTitle>
      <PageSubtitle>
        {t('products.kipaWallet.security.subtitle', '我们将安全放在首位，Kipa钱包采用多层保护机制确保您的数字资产安全无忧')}
      </PageSubtitle>
      
      {/* 安全特性卡片网格 */}
      <SecurityFeaturesGrid>
        {/* 多重签名特性 */}
        <SecurityFeatureCard variants={itemVariants}>
          <FeatureIconContainer>
            🔒
          </FeatureIconContainer>
          <FeatureTitle>{t('products.kipaWallet.security.multiSig.title', '多重签名')}</FeatureTitle>
          <FeatureDescription>
            {t('products.kipaWallet.security.multiSig.description', '支持多重签名功能，一笔交易需要多个密钥共同授权，大幅提高安全性，防止单点故障。')}
          </FeatureDescription>
        </SecurityFeatureCard>
        
        {/* 生物识别特性 */}
        <SecurityFeatureCard variants={itemVariants}>
          <FeatureIconContainer>
            👆
          </FeatureIconContainer>
          <FeatureTitle>{t('products.kipaWallet.security.biometrics.title', '生物识别')}</FeatureTitle>
          <FeatureDescription>
            {t('products.kipaWallet.security.biometrics.description', '支持指纹和面部识别等生物认证方式，提供方便快捷且安全的钱包解锁体验。')}
          </FeatureDescription>
        </SecurityFeatureCard>
        
        {/* 冷钱包存储特性 */}
        <SecurityFeatureCard variants={itemVariants}>
          <FeatureIconContainer>
            🛡️
          </FeatureIconContainer>
          <FeatureTitle>{t('products.kipaWallet.security.coldStorage.title', '冷钱包存储')}</FeatureTitle>
          <FeatureDescription>
            {t('products.kipaWallet.security.coldStorage.description', '支持将大额资产存储在离线冷钱包中，物理隔离网络攻击风险，保障资产绝对安全。')}
          </FeatureDescription>
        </SecurityFeatureCard>
        
        {/* 隐私保护特性 */}
        <SecurityFeatureCard variants={itemVariants}>
          <FeatureIconContainer>
            🕵️
          </FeatureIconContainer>
          <FeatureTitle>{t('products.kipaWallet.security.privacy.title', '隐私保护')}</FeatureTitle>
          <FeatureDescription>
            {t('products.kipaWallet.security.privacy.description', '内置隐私保护功能，可选择性地隐藏交易记录和资产数量，保护您的财务隐私。')}
          </FeatureDescription>
        </SecurityFeatureCard>
        
        {/* 二次验证特性 */}
        <SecurityFeatureCard variants={itemVariants}>
          <FeatureIconContainer>
            📱
          </FeatureIconContainer>
          <FeatureTitle>{t('products.kipaWallet.security.twoFactor.title', '二次验证')}</FeatureTitle>
          <FeatureDescription>
            {t('products.kipaWallet.security.twoFactor.description', '支持多种二次验证方式，包括短信、电子邮件和认证器应用，提供额外安全层。')}
          </FeatureDescription>
        </SecurityFeatureCard>
        
        {/* 自动锁定特性 */}
        <SecurityFeatureCard variants={itemVariants}>
          <FeatureIconContainer>
            ⏱️
          </FeatureIconContainer>
          <FeatureTitle>{t('products.kipaWallet.security.autoLock.title', '自动锁定')}</FeatureTitle>
          <FeatureDescription>
            {t('products.kipaWallet.security.autoLock.description', '钱包在指定时间内无活动将自动锁定，防止未授权访问，可根据个人需求自定义锁定时间。')}
          </FeatureDescription>
        </SecurityFeatureCard>
      </SecurityFeaturesGrid>
      
      {/* 安全提示部分 */}
      <SecurityTipsSection>
        <TipsTitle>{t('products.kipaWallet.security.tipsTitle', '钱包安全使用建议')}</TipsTitle>
        <TipsList>
          <TipItem>
            <TipNumber>1</TipNumber>
            <TipText>{t('products.kipaWallet.security.tip1', '妥善保管您的助记词，最好离线存储在安全地点，切勿与他人共享。')}</TipText>
          </TipItem>
          <TipItem>
            <TipNumber>2</TipNumber>
            <TipText>{t('products.kipaWallet.security.tip2', '启用所有可用的安全功能，如生物识别和二次验证，提高账户安全性。')}</TipText>
          </TipItem>
          <TipItem>
            <TipNumber>3</TipNumber>
            <TipText>{t('products.kipaWallet.security.tip3', '定期更新钱包应用，确保获得最新的安全补丁和功能改进。')}</TipText>
          </TipItem>
          <TipItem>
            <TipNumber>4</TipNumber>
            <TipText>{t('products.kipaWallet.security.tip4', '小心钓鱼网站和应用，始终从官方渠道下载Kipa钱包应用。')}</TipText>
          </TipItem>
          <TipItem>
            <TipNumber>5</TipNumber>
            <TipText>{t('products.kipaWallet.security.tip5', '使用强密码保护您的钱包，避免在多个平台使用相同密码。')}</TipText>
          </TipItem>
        </TipsList>
      </SecurityTipsSection>
    </SecurityContainer>
  );
};

export default KipaWalletSecurity; 